import { z } from 'zod/v4';

import { HistoricalOfferDTOSchema, TimeWindowRuntype } from './backendTypes';

export const OfferColumnRuntype = z.enum([
    'id',
    'part',
    'manufacturer',
    'supplier',
    'stock_location',
    'time',
    'total_stock',
    'lead_time_days',
    'unit_price_1',
    'unit_price_100',
    'unit_price_1k',
    'unit_price_10k',
    'unit_price_50k',
    'origin',
    'price_type',
    'original_currency',
    'packaging',
    'validity',
    'classification_rule',
]);
export type OfferColumn = z.infer<typeof OfferColumnRuntype>;

export const OfferFilterKeyRuntype = z.enum([...OfferColumnRuntype.options, 'supplier_id']);
export type OfferFilterKey = z.infer<typeof OfferFilterKeyRuntype>;

export const OFFER_NUMBER_COLUMNS = [
    'total_stock',
    'lead_time_days',
    'unit_price_1',
    'unit_price_100',
    'unit_price_1k',
    'unit_price_10k',
    'unit_price_50k',
] as const;

const blankFilter = z.object({ type: z.enum(['blank', 'notBlank']) });
export const textFilter = z.intersection(
    z.object({ filterType: z.literal('text') }),
    z.union([
        blankFilter,
        z.object({ type: z.enum(['in', 'notIn']), values: z.array(z.string().max(500)).min(1).max(300) }),
        z.object({
            type: z.enum(['contains', 'notContains', 'equals', 'notEqual', 'startsWith', 'endsWith']),
            filter: z.string().max(500),
        }),
    ]),
);
export const numberFilter = z.intersection(
    z.object({ filterType: z.literal('number') }),
    z.union([
        blankFilter,
        z.object({
            type: z.enum(['equals', 'notEqual', 'lessThan', 'lessThanOrEqual', 'greaterThan', 'greaterThanOrEqual']),
            filter: z.number(),
        }),
        z.object({ type: z.literal('inRange'), filter: z.number(), filterTo: z.number() }),
    ]),
);
const dateFilter = z.intersection(
    z.object({ filterType: z.literal('date') }),
    z.union([
        blankFilter,
        z.object({ type: z.enum(['equals', 'notEqual', 'lessThan', 'greaterThan']), dateFrom: z.iso.date() }),
        z.object({ type: z.literal('inRange'), dateFrom: z.iso.date(), dateTo: z.iso.date() }),
    ]),
);
export const OfferFilterConditionRuntype = z.union([textFilter, numberFilter, dateFilter]);
export type OfferFilterCondition = z.infer<typeof OfferFilterConditionRuntype>;
export const OfferFilterRuntype = z.union([
    OfferFilterConditionRuntype,
    z.object({
        operator: z.enum(['AND', 'OR']),
        conditions: z.array(OfferFilterConditionRuntype).min(1).max(2),
    }),
]);
export type OfferFilter = z.infer<typeof OfferFilterRuntype>;

export const MAX_OFFER_PARTS = 50;
export const OfferPartIdsRuntype = z
    .array(z.uuid())
    .min(1)
    .max(MAX_OFFER_PARTS)
    .transform((ids) => [...new Set(ids.map((id) => id.toLowerCase()))]);

export const BrowserRequestRuntype = z
    .object({
        timeWindow: TimeWindowRuntype.refine(
            ({ from, to }) => {
                const start = new Date(from).getTime();
                const end = new Date(to).getTime();
                const day = 24 * 60 * 60 * 1000;
                return (
                    end - start >= day &&
                    end - start <= 366 * day &&
                    start % day === 0 &&
                    end % day === 0 &&
                    end <= Math.floor(Date.now() / day) * day + day
                );
            },
            { message: 'Offers require a 1–366-day UTC-midnight window ending no later than next UTC midnight' },
        ),
        partIds: OfferPartIdsRuntype,
        asOf: z.iso
            .datetime({ offset: true })
            .refine((value) => new Date(value).getTime() <= Date.now(), {
                message: 'asOf cannot be in the future',
            })
            .optional(),
        offset: z.number().int().min(0).default(0),
        limit: z.number().int().min(1).max(1000).default(100),
        sort: z
            .array(z.object({ column: OfferColumnRuntype, direction: z.enum(['asc', 'desc']) }))
            .max(19)
            .default([]),
        // Supplier IDs are filter-only; the supplier column still displays and sorts names.
        filters: z.partialRecord(OfferFilterKeyRuntype, OfferFilterRuntype).default({}),
    })
    .refine(
        ({ filters }) =>
            Object.entries(filters).every(([column, model]) => {
                if (!model) return true;
                const expectedType =
                    column === 'time'
                        ? 'date'
                        : (OFFER_NUMBER_COLUMNS as readonly string[]).includes(column)
                          ? 'number'
                          : 'text';
                return ('conditions' in model ? model.conditions : [model]).every((entry) => {
                    if (entry.filterType !== expectedType) return false;
                    if (column !== 'supplier_id') return true;
                    return (
                        entry.filterType === 'text' &&
                        (entry.type === 'blank' ||
                            entry.type === 'notBlank' ||
                            ((entry.type === 'in' || entry.type === 'notIn') &&
                                entry.values.every((value) => z.uuid().safeParse(value).success)))
                    );
                });
            }),
        { message: 'Filter type must match the offer column' },
    );
export const RequestBrowseOffersRuntype = BrowserRequestRuntype.safeExtend({
    detailScope: z
        .object({
            partId: z.uuid().transform((id) => id.toLowerCase()),
            supplierId: z.uuid().transform((id) => id.toLowerCase()),
            stockLocationId: z.uuid().transform((id) => id.toLowerCase()),
        })
        .optional(),
}).refine(({ partIds, detailScope }) => !detailScope || (partIds.length === 1 && partIds[0] === detailScope.partId), {
    message: 'Detail scope must match the single selected part',
});
export type RequestBrowseOffers = z.input<typeof RequestBrowseOffersRuntype>;

/** Prices remain in the database's normalized EUR currency, including when the source currency differs. */
export const OfferRowSchema = HistoricalOfferDTOSchema.omit({
    part: true,
    stock_location: true,
    classified_by_user_id: true,
    total_stock: true,
}).extend({
    total_stock: z.number().nullable(),
    part: z.string().nullable(),
    manufacturer: z.string().nullable(),
    supplier: z.string().nullable(),
    /** Region code, not a localized display name. */
    stock_location: z.string().nullable(),
});
export type OfferRow = z.infer<typeof OfferRowSchema>;
export const BrowseOffersResultSchema = z.object({
    rows: z.array(OfferRowSchema),
    hasMore: z.boolean(),
});
export type BrowseOffersResult = z.infer<typeof BrowseOffersResultSchema>;
